"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useSelectedLayoutSegment, usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { useSession, signOut } from "next-auth/react";

import { docsConfig } from "@/config/docs";
import { marketingConfig } from "@/config/marketing";
import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";
import { DocsSidebarNav } from "@/components/docs/sidebar-nav";
import { Icons } from "@/components/shared/icons";
import { clearClientStorageOnLogout } from "@/lib/logout";

import { ModeToggle } from "./mode-toggle";

// Conversion landing pages render a stripped-down nav (logo + $1 trial button
// only, handled by NavBar), so the mobile hamburger menu is suppressed here.
const MINIMAL_NAV_PATHS = ["/start", "/products/options-journal"];

export function NavMobile() {
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const selectedLayout = useSelectedLayoutSegment();
  const pathname = usePathname();
  const documentation = selectedLayout === "docs";
  const isMinimal = !!pathname && MINIMAL_NAV_PATHS.includes(pathname);
  
  // Determine login URL with callback
  const getLoginUrl = () => {
    if (pathname === "/pricing" || pathname.startsWith("/pricing")) {
      return "/login?callbackUrl=/pricing";
    }
    return "/login";
  };

  const configMap = {
    docs: docsConfig.mainNav,
  };

  const links =
    (selectedLayout && configMap[selectedLayout]) || marketingConfig.mainNav;

  // prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [open]);

  // On conversion landing pages the NavBar already shows the single trial CTA
  // (visible on mobile too), so we suppress the hamburger menu entirely.
  if (isMinimal) return null;

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "fixed right-2 top-2.5 z-50 rounded-full p-2 transition-colors duration-200 hover:bg-muted focus:outline-none active:bg-muted md:hidden",
          open && "hover:bg-muted active:bg-muted",
        )}
      >
        {open ? (
          <X className="size-5 text-muted-foreground" />
        ) : (
          <Menu className="size-5 text-muted-foreground" />
        )}
      </button>

      <nav
        className={cn(
          "fixed inset-0 z-20 hidden w-full overflow-auto bg-background px-5 py-16 lg:hidden",
          open && "block",
        )}
      >
        <ul className="grid divide-y divide-muted">
          {links && links.length > 0 && links.map((item, index) => (
            item.items && item.items.length > 0 ? (
              // Dropdown menu item for mobile - with icons & tags
              <li key={`${item.title}-${index}`} className="py-3">
                <div className="flex flex-col space-y-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 mb-1">
                    {item.title}
                  </span>
                  <ul className="space-y-0.5">
                    {item.items.map((subItem, subIndex) => {
                      const SubIcon = subItem.icon ? Icons[subItem.icon] : null;
                      return (
                        <li key={`${subItem.href}-${subIndex}`}>
                          <Link
                            href={subItem.href}
                            onClick={() => setOpen(false)}
                            className={cn(
                              "flex items-center gap-3 rounded-lg px-2 py-2 text-sm transition-colors hover:bg-muted/60",
                              subItem.disabled && "opacity-50 pointer-events-none"
                            )}
                          >
                            {SubIcon && (
                              <div className="p-1.5 rounded-md bg-primary/10 text-primary shrink-0">
                                <SubIcon className="size-4" />
                              </div>
                            )}
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="font-medium truncate">{subItem.title}</span>
                              {subItem.tag && (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary leading-none shrink-0">
                                  {subItem.tag}
                                </span>
                              )}
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </li>
            ) : (
              // Regular link item
              <li key={`${item.href}-${index}`} className="py-3">
                <Link
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="flex w-full font-medium capitalize"
                >
                  {item.title}
                </Link>
              </li>
            )
          ))}

          {session ? (
            <>
              {session.user.role === "ADMIN" ? (
                <li className="py-3">
                  <Link
                    href="/dashboard/admin"
                    onClick={() => setOpen(false)}
                    className="flex w-full font-medium capitalize"
                  >
                    Admin
                  </Link>
                </li>
              ) : null}

              <li className="py-3">
                <Link
                  href="/dashboard"
                  onClick={() => setOpen(false)}
                  className="flex w-full font-medium capitalize"
                >
                  Dashboard
                </Link>
              </li>

              <li className="py-3">
                <button
                  onClick={() => {
                    setOpen(false);
                    clearClientStorageOnLogout();
                    signOut();
                  }}
                  className="flex w-full font-medium capitalize text-left"
                >
                  Log out
                </button>
              </li>
            </>
          ) : (
            <>
              <li className="py-3">
                <Link
                  href={getLoginUrl()}
                  onClick={() => setOpen(false)}
                  className="flex w-full font-medium capitalize"
                >
                  Login
                </Link>
              </li>

              <li className="py-3">
                <Link
                  href="/register"
                  onClick={() => setOpen(false)}
                  className="flex w-full font-medium capitalize"
                >
                  Sign up
                </Link>
              </li>
            </>
          )}
        </ul>

        {documentation ? (
          <div className="mt-8 block md:hidden">
            <DocsSidebarNav setOpen={setOpen} />
          </div>
        ) : null}

        <div className="mt-5 flex items-center justify-end space-x-4">
          <Link href={siteConfig.links.github} target="_blank" rel="noreferrer">
            <Icons.gitHub className="size-6" />
            <span className="sr-only">GitHub</span>
          </Link>
          <ModeToggle />
        </div>
      </nav>
    </>
  );
}
